import React, { useMemo, useState } from 'react';
import { Box, Button, FormControlLabel, IconButton, Switch, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { OBSIDIAN, OBSIDIAN_BODY_FONT } from '../../constants/obsidianDashboardTheme';
import type { OfficeHoursDateOverride } from '../../utils/sandboxOfficeHoursApi';
import { officeHoursFieldSx } from './SandboxOfficeHoursWeeklyEditor';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function ymd(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function getMonthGrid(year: number, monthIndex: number): (Date | null)[][] {
    const first = new Date(year, monthIndex, 1);
    const last = new Date(year, monthIndex + 1, 0);
    const startPad = (first.getDay() + 6) % 7;
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, monthIndex, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const rows: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
}

type Props = {
    value: OfficeHoursDateOverride[];
    onChange: (next: OfficeHoursDateOverride[]) => void;
};

/**
 * Meeteor-style date overrides: month picker + per-date cards (Obsidian).
 */
export function SandboxOfficeHoursOverridesEditor({ value, onChange }: Props) {
    const [month, setMonth] = useState(() => {
        const n = new Date();
        return { y: n.getFullYear(), m: n.getMonth() };
    });

    const grid = useMemo(() => getMonthGrid(month.y, month.m), [month.y, month.m]);
    const existing = useMemo(() => new Set(value.map(v => v.date)), [value]);
    const monthLabel = useMemo(
        () => new Date(month.y, month.m, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' }),
        [month.y, month.m],
    );

    const addDate = (ds: string) => {
        if (existing.has(ds)) return;
        onChange(
            [
                ...value,
                { date: ds, available: true, slots: [{ start: '09:00', end: '17:00', price_amount: null }] },
            ].sort((a, b) => a.date.localeCompare(b.date)),
        );
    };

    const patchRow = (idx: number, patch: Partial<OfficeHoursDateOverride>) => {
        const next = [...value];
        next[idx] = { ...next[idx], ...patch };
        onChange(next);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${OBSIDIAN.outline}44`,
                    bgcolor: OBSIDIAN.surfaceContainer,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Button
                        size="small"
                        onClick={() =>
                            setMonth(prev => (prev.m === 0 ? { y: prev.y - 1, m: 11 } : { y: prev.y, m: prev.m - 1 }))
                        }
                        sx={{ textTransform: 'none', color: OBSIDIAN.primary, minWidth: 0 }}>
                        ‹
                    </Button>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: OBSIDIAN.onSurface }}>
                        {monthLabel}
                    </Typography>
                    <Button
                        size="small"
                        onClick={() =>
                            setMonth(prev => (prev.m === 11 ? { y: prev.y + 1, m: 0 } : { y: prev.y, m: prev.m + 1 }))
                        }
                        sx={{ textTransform: 'none', color: OBSIDIAN.primary, minWidth: 0 }}>
                        ›
                    </Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                    {WEEKDAYS.map(d => (
                        <Typography
                            key={d}
                            sx={{
                                textAlign: 'center',
                                fontSize: 10,
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            {d}
                        </Typography>
                    ))}
                </Box>
                {grid.map((row, ri) => (
                    <Box key={ri} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                        {row.map((cell, ci) => {
                            if (!cell) return <Box key={ci} />;
                            const ds = ymd(cell);
                            const has = existing.has(ds);
                            return (
                                <Button
                                    key={ds}
                                    onClick={() => addDate(ds)}
                                    sx={{
                                        minWidth: 0,
                                        height: 32,
                                        p: 0,
                                        borderRadius: 1.5,
                                        fontSize: 12,
                                        fontWeight: has ? 800 : 500,
                                        color: has ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurface,
                                        bgcolor: has ? OBSIDIAN.primary : 'transparent',
                                        border: has ? 'none' : `1px solid ${OBSIDIAN.outline}33`,
                                        '&:hover': {
                                            bgcolor: has ? OBSIDIAN.primary : `${OBSIDIAN.primary}22`,
                                        },
                                    }}>
                                    {cell.getDate()}
                                </Button>
                            );
                        })}
                    </Box>
                ))}
                <Typography sx={{ mt: 1, fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                    Tap a day to override weekly hours
                </Typography>
            </Box>

            {value.map((row, idx) => (
                <Box
                    key={row.date}
                    sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${OBSIDIAN.outline}33`,
                        bgcolor: OBSIDIAN.surfaceContainer,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: OBSIDIAN.onSurface }}>
                            {row.date}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={row.available}
                                        onChange={e => patchRow(idx, { available: e.target.checked })}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: OBSIDIAN.primary },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                bgcolor: OBSIDIAN.primary,
                                            },
                                        }}
                                    />
                                }
                                label={row.available ? 'Open' : 'Blocked'}
                                sx={{
                                    m: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 12,
                                        color: OBSIDIAN.onSurfaceVariant,
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => onChange(value.filter((_, i) => i !== idx))}
                                sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                    {row.available ? (
                        row.slots.map((slot, si) => (
                            <Box
                                key={`${row.date}-${si}`}
                                sx={{
                                    mt: 1,
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 72px auto',
                                    gap: 1,
                                }}>
                                <TextField
                                    size="small"
                                    type="time"
                                    value={slot.start}
                                    onChange={e => {
                                        const slots = [...row.slots];
                                        slots[si] = { ...slots[si], start: e.target.value };
                                        patchRow(idx, { slots });
                                    }}
                                    sx={officeHoursFieldSx}
                                />
                                <TextField
                                    size="small"
                                    type="time"
                                    value={slot.end}
                                    onChange={e => {
                                        const slots = [...row.slots];
                                        slots[si] = { ...slots[si], end: e.target.value };
                                        patchRow(idx, { slots });
                                    }}
                                    sx={officeHoursFieldSx}
                                />
                                <TextField
                                    size="small"
                                    placeholder="$"
                                    value={slot.price_amount == null ? '' : String(slot.price_amount)}
                                    onChange={e => {
                                        const raw = e.target.value.trim();
                                        const n = raw === '' ? null : Number(raw);
                                        const slots = [...row.slots];
                                        slots[si] = {
                                            ...slots[si],
                                            price_amount: n != null && Number.isFinite(n) ? n : null,
                                        };
                                        patchRow(idx, { slots });
                                    }}
                                    sx={officeHoursFieldSx}
                                />
                                <IconButton
                                    size="small"
                                    disabled={row.slots.length <= 1}
                                    onClick={() => patchRow(idx, { slots: row.slots.filter((_, i) => i !== si) })}
                                    sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))
                    ) : (
                        <Typography sx={{ mt: 1, fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                            Blocked — no bookings this day
                        </Typography>
                    )}
                    {row.available ? (
                        <Button
                            size="small"
                            onClick={() =>
                                patchRow(idx, {
                                    slots: [...row.slots, { start: '09:00', end: '17:00', price_amount: null }],
                                })
                            }
                            sx={{
                                mt: 1,
                                textTransform: 'none',
                                color: OBSIDIAN.primary,
                                fontSize: 12,
                                fontWeight: 600,
                            }}>
                            + Add time range
                        </Button>
                    ) : null}
                </Box>
            ))}
        </Box>
    );
}
